/**
 * 美洽网页客服（https://www.meiqia.cn / JS 插件文档）
 * - 官方插件默认支持文字、图片；移动端 HTTPS 下通常支持语音
 * - 每次页面加载使用全新 clientId，并清理本地缓存，实现「刷新后清空旧消息」
 */

const SCRIPT_ID = 'meiqia-js-sdk';
const STORAGE_KEY_RE = /meiqia|MEIQIA|MEG[_-]|mq[_-]/i;

let loadPromise = null;
let ready = false;
let initPromise = null;

function freshClientId() {
  // 美洽要求 6–32 位唯一字符串
  const raw =
    'g' +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 12);
  return raw.slice(0, 32);
}

/** 清除美洽在浏览器中的会话/未读等缓存，避免刷新后带回旧聊天记录 */
export function clearMeiqiaLocalData() {
  try {
    [window.localStorage, window.sessionStorage].forEach((storage) => {
      if (!storage) return;
      const keys = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && STORAGE_KEY_RE.test(key)) keys.push(key);
      }
      keys.forEach((key) => storage.removeItem(key));
    });
  } catch (e) {
    console.warn('[meiqia] clear storage failed', e);
  }
}

function ensureMeiqiaQueue() {
  const w = window;
  if (typeof w._MEIQIA === 'function' && Array.isArray(w._MEIQIA.a)) {
    return w._MEIQIA;
  }
  const q = function () {
    (q.a = q.a || []).push(arguments);
  };
  q.a = w._MEIQIA && w._MEIQIA.a ? w._MEIQIA.a : [];
  w._MEIQIA = q;
  return q;
}

function loadMeiqiaScript() {
  if (document.getElementById(SCRIPT_ID)) {
    return Promise.resolve();
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    ensureMeiqiaQueue();
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.charset = 'UTF-8';
    script.src = 'https://static.meiqia.com/dist/meiqia.js';
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('failed to load meiqia.js'));
    };
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}

/**
 * 在首页初始化美洽。默认每次调用会清本地缓存并换新 clientId（适合整页刷新场景）。
 * @param {{ entId?: string, clearHistory?: boolean }} options
 */
export async function initMeiqia(options = {}) {
  const entId =
    options.entId ||
    process.env.VUE_APP_MEIQIA_ENT_ID ||
    '';

  if (!entId) {
    console.warn(
      '[meiqia] 未配置 VUE_APP_MEIQIA_ENT_ID，请到美洽工作台「设置 → 接入 → 网页插件」复制企业 ID'
    );
    return false;
  }

  // 同一次页面生命周期内只初始化一次；刷新会整页重载并重新清会话
  if (ready) {
    return true;
  }
  if (initPromise) {
    return initPromise;
  }

  const clearHistory = options.clearHistory !== false;
  if (clearHistory) {
    clearMeiqiaLocalData();
  }

  initPromise = (async () => {
    try {
      await loadMeiqiaScript();
    } catch (e) {
      console.error('[meiqia] script load error', e);
      initPromise = null;
      return false;
    }

    const MQ = ensureMeiqiaQueue();
    const clientId = freshClientId();

    return new Promise((resolve) => {
      let settled = false;
      const done = (ok) => {
        if (settled) return;
        settled = true;
        ready = !!ok;
        if (!ok) initPromise = null;
        resolve(!!ok);
      };

      MQ('entId', String(entId));
      MQ('manualInit');
      MQ('clientId', clientId);
      MQ('allSet', () => done(true));
      MQ('init');

      // 防止 allSet 未触发时一直挂起
      setTimeout(() => done(true), 8000);
    });
  })();

  return initPromise;
}

export function showMeiqiaPanel(opts) {
  if (typeof window._MEIQIA !== 'function') {
    console.warn('[meiqia] 尚未初始化');
    return;
  }
  if (opts) {
    window._MEIQIA('showPanel', opts);
  } else {
    window._MEIQIA('showPanel');
  }
}

export function isMeiqiaReady() {
  return ready;
}
